// External imports
import { InferRequestType, InferResponseType } from "hono";

// Internal imports
import { client } from "@/lib/hono";
import {
  CreateListRequest,
  UpdateListRequest
} from "@/types/lists.types";

// API types based on backend routes
type CreateListAPI = typeof client.api.lists.$post;
type GetListsAPI = typeof client.api.lists.$get;
type GetListByIdAPI = typeof client.api.lists[":id"]["$get"];
type UpdateListAPI = typeof client.api.lists[":id"]["$put"];
type GetListStatsAPI = typeof client.api.lists["stats"]["$get"];
type GetRecentItemsAPI = typeof client.api.lists["recent-items"]["$get"];

// Request types
export type CreateListRequestType = InferRequestType<CreateListAPI>["json"];
export type UpdateListRequestType = InferRequestType<UpdateListAPI>["json"];

// Response types (can be used for typing if needed)
export type GetListsResponseType = InferResponseType<GetListsAPI>;
export type GetListByIdResponseType = InferResponseType<GetListByIdAPI>;
export type GetListStatsResponseType = InferResponseType<GetListStatsAPI>;
export type GetRecentItemsResponseType = InferResponseType<GetRecentItemsAPI>;

/**
 * Lists API service
 */
export const listsService = {
  /**
   * Create a new list
   */
  async createList(data: CreateListRequest) {
    const response = await client.api.lists.$post({
      json: data,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create list");
    }

    return response.json();
  },

  /**
   * Get all user's lists
   */
  async getLists() {
    const response = await client.api.lists.$get();

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch lists");
    }

    return response.json();
  },

  /**
   * Get all featured lists from all users
   */
  async getFeaturedLists() {
    const response = await client.api.lists.featured.$get();

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch featured lists");
    }

    return response.json();
  },

  /**
   * Get a specific list by ID with all items
   */
  /** Mints or returns the list's share token. */
  async shareList(listId: string): Promise<{ token: string }> {
    const response = await client.api.lists[":id"].share.$post({ param: { id: listId } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as { error?: string }).error || "Failed to share list");
    }
    const result = (await response.json()) as { success: boolean; data?: { token: string } };
    if (!result.success || !result.data) throw new Error("Failed to share list");
    return result.data;
  },

  async getListById(listId: string) {
    const response = await client.api.lists[":id"].$get({
      param: { id: listId },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch list");
    }

    return response.json();
  },

  /**
   * Update a list
   */
  async updateList(listId: string, data: UpdateListRequest) {
    const response = await client.api.lists[":id"].$put({
      param: { id: listId },
      json: data,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to update list");
    }

    return response.json();
  },

  /**
   * Delete a list
   */
  async deleteList(listId: string) {
    const response = await client.api.lists[":id"].$delete({
      param: { id: listId },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to delete list");
    }

    return response.json();
  },

  /**
   * Add a restaurant to a list
   */
  async addRestaurantToList(listId: string, data: { restaurantId: string }) {
    const response = await client.api.lists[":id"]["items"].$post({
      param: { id: listId },
      json: data,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to add restaurant to list");
    }

    return response.json();
  },

  /**
   * Remove a restaurant from a list
   */
  async removeRestaurantFromList(listId: string, data: { restaurantId: string }) {
    const response = await client.api.lists[":id"]["items"].$delete({
      param: { id: listId },
      json: data,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to remove restaurant from list");
    }

    return response.json();
  },

  /**
   * Check if a restaurant is in a specific list
   */
  async checkRestaurantInList(listId: string, restaurantId: string) {
    const response = await client.api.lists[":id"]["items"][":restaurantId"]["check"].$get({
      param: { id: listId, restaurantId },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to check restaurant in list");
    }

    return response.json();
  },

  /**
   * Get all lists containing a specific restaurant
   */
  async getListsByRestaurant(restaurantId: string) {
    const response = await client.api.lists["by-restaurant"][":restaurantId"].$get({
      param: { restaurantId },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch lists by restaurant");
    }

    return response.json();
  },

  /**
   * Get user's list statistics
   */
  async getListStats() {
    const response = await client.api.lists["stats"].$get();

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch list statistics");
    }

    return response.json();
  },

  /**
   * Get recent items added to lists
   */
  async getRecentItems(limit?: number) {
    const query = limit ? { limit: limit.toString() } : {};
    const response = await client.api.lists["recent-items"].$get({
      query,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error((error as any).error || "Failed to fetch recent items");
    }

    return response.json();
  },
};